/**
 * Blueprint-driven "generic" filter plugins. One class per value-kind
 * (LIST / RANGE / FLAG) covers every clinic condition of that kind — id,
 * label and options-source are carried from the API blueprint instead of a
 * hand-written per-condition plugin.
 *
 * These are the FALLBACK branch of the hybrid resolver: used only when no
 * existing plugin is registered for a blueprint's condition id.
 */
import type {
  BdCondition,
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionExpression } from '../filter';
import { FilterConditionUtil } from '../filter.util';
import type {
  ClinicConditionBlueprint,
  ClinicConditionTarget,
} from './blueprint.model';
import {
  blueprintDisplayName,
  findFragment,
} from './blueprint.model';
import type {
  ClinicOperatingNumberComparison,
} from './generic-conditions';
import {
  CLINIC_OPERATING_NUMBER_COMPARISON_SYMBOLS,
  GenericContentCondition,
  GenericFlagCondition,
  GenericOperatingNumberCondition,
  GenericRangeCondition,
  GenericSubstringCondition,
  GenericWrapperCondition,
} from './generic-conditions';
import type { FilterDetailedText } from '../filter-detailed-text';
import { FILTER_RESOURCE_CLINIC_OPERATING_NUMBER } from '../filter-resource-identifiers';
import type { ShareableType } from '@bindoc-projects/resource-domain-lib';

/** Inner grain of each wrapper condition (the leaves inside are of this grain). */
export const WRAPPER_INNER_GRAINS: Record<string, ClinicConditionTarget> = {
  department: 'DEPARTMENT',
  deqs: 'DEQS',
};

export type GenericListValue = { includes: string[]; excludes: string[] };
export type GenericRangeValue = { from: number | null; to: number | null };
export type GenericOperatingNumberValue = {
  operatingNumberId: string | null;
  comparison: ClinicOperatingNumberComparison;
  value: number | null;
};
export type GenericPluginValue =
  | GenericListValue
  | GenericRangeValue
  | GenericOperatingNumberValue
  | FilterConditionExpression
  | boolean
  | string;

/** Persisted plugin state (this plugin's own `toJSON`). */
export interface GenericPluginDto {
  type: string;
  includes?: string[];
  excludes?: string[];
  from?: number | null;
  to?: number | null;
  flag?: boolean;
  value?: string | null;
  operatingNumberId?: string | null;
  comparison?: ClinicOperatingNumberComparison;
  operatingNumberValue?: number | null;
  condition?: unknown;
}

/** Inner object of a serialized `BdCondition` (used when re-hydrating). */
export interface GenericConditionInner {
  includes?: string[] | null;
  excludes?: string[] | null;
  from?: number | null;
  to?: number | null;
  flag?: boolean;
  value?: string | number | null;
  operatingNumberId?: string | null;
  comparison?: ClinicOperatingNumberComparison;
  /** Nested condition tree of a wrapper (department/deqs) condition. */
  condition?: FilterConditionDto | null;
}

type GenericSpecification = FilterConditionPluginSpecification<
  GenericPluginValue,
  GenericPluginDto,
  GenericConditionInner
>;

export class GenericListPlugin extends FilterConditionPluginImpl<
  GenericListValue,
  GenericPluginDto
> {
  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    private includes: string[] = [],
    private excludes: string[] = [],
  ) {
    super(blueprint.id);
  }

  clone(): GenericListPlugin {
    return new GenericListPlugin(
      this.blueprint,
      [...this.includes],
      [...this.excludes],
    );
  }

  /** New instance for the same blueprint with the given selection. */
  withValue(includes: string[], excludes: string[] = []): GenericListPlugin {
    return new GenericListPlugin(this.blueprint, [...includes], [...excludes]);
  }

  toCondition(): BdCondition | null {
    return new GenericContentCondition(
      this.blueprint.id,
      this.includes,
      this.excludes,
    );
  }

  toJSON(): GenericPluginDto | null {
    return { type: this.type, includes: this.includes, excludes: this.excludes };
  }

  toText(): string | null {
    const count = this.includes.length + this.excludes.length;
    if (count === 0) return null;
    return count === 1
      ? `1 ${blueprintDisplayName(this.blueprint)}`
      : `${count} ${blueprintDisplayName(this.blueprint)}`;
  }

  toValue(): GenericListValue {
    return { includes: this.includes, excludes: this.excludes };
  }

  override valid(): boolean {
    return this.includes.length > 0 || this.excludes.length > 0;
  }

  validationToText(): string {
    return this.valid() ? '' : 'Mindestens einen Wert auswählen';
  }
}

export class GenericRangePlugin extends FilterConditionPluginImpl<
  GenericRangeValue,
  GenericPluginDto
> {
  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    private from: number | null = null,
    private to: number | null = null,
  ) {
    super(blueprint.id);
  }

  clone(): GenericRangePlugin {
    return new GenericRangePlugin(this.blueprint, this.from, this.to);
  }

  /** New instance for the same blueprint with the given range. */
  withValue(from: number | null, to: number | null): GenericRangePlugin {
    return new GenericRangePlugin(this.blueprint, from, to);
  }

  toCondition(): BdCondition | null {
    return new GenericRangeCondition(this.blueprint.id, this.from, this.to);
  }

  toJSON(): GenericPluginDto | null {
    return { type: this.type, from: this.from, to: this.to };
  }

  toText(): string | null {
    const label = blueprintDisplayName(this.blueprint);
    if (this.from != null && this.to != null) {
      return `${label}: ${this.from}–${this.to}`;
    }
    if (this.from != null) return `${label}: ab ${this.from}`;
    if (this.to != null) return `${label}: bis ${this.to}`;
    return null;
  }

  toValue(): GenericRangeValue {
    return { from: this.from, to: this.to };
  }

  override valid(): boolean {
    return this.from != null || this.to != null;
  }

  validationToText(): string {
    return this.valid() ? '' : 'Min oder Max auswählen';
  }
}

export class GenericFlagPlugin extends FilterConditionPluginImpl<
  boolean,
  GenericPluginDto
> {
  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    private flag = true,
  ) {
    super(blueprint.id);
  }

  clone(): GenericFlagPlugin {
    return new GenericFlagPlugin(this.blueprint, this.flag);
  }

  /** New instance for the same blueprint with the given flag. */
  withValue(flag: boolean): GenericFlagPlugin {
    return new GenericFlagPlugin(this.blueprint, flag);
  }

  toCondition(): BdCondition | null {
    return new GenericFlagCondition(this.blueprint.id, this.flag);
  }

  toJSON(): GenericPluginDto | null {
    return { type: this.type, flag: this.flag };
  }

  toText(): string | null {
    const label = blueprintDisplayName(this.blueprint);
    return this.flag ? `mit ${label}` : `ohne ${label}`;
  }

  toValue(): boolean {
    return this.flag;
  }

  override valid(): boolean {
    return this.flag === true || this.flag === false;
  }

  validationToText(): string {
    return '';
  }
}

export class GenericSubstringPlugin extends FilterConditionPluginImpl<
  string,
  GenericPluginDto
> {
  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    private value = '',
  ) {
    super(blueprint.id);
  }

  clone(): GenericSubstringPlugin {
    return new GenericSubstringPlugin(this.blueprint, this.value);
  }

  /** New instance for the same blueprint with the given text. */
  withValue(value: string): GenericSubstringPlugin {
    return new GenericSubstringPlugin(this.blueprint, value);
  }

  toCondition(): BdCondition | null {
    return new GenericSubstringCondition(this.blueprint.id, this.value);
  }

  toJSON(): GenericPluginDto | null {
    return { type: this.type, value: this.value };
  }

  toText(): string | null {
    const text = this.value?.trim();
    if (!text) return null;
    return `${blueprintDisplayName(this.blueprint)}: „${text}"`;
  }

  toValue(): string {
    return this.value;
  }

  override valid(): boolean {
    return !!this.value && this.value.trim().length > 0;
  }

  validationToText(): string {
    return this.valid() ? '' : 'Suchbegriff eingeben';
  }
}

export class GenericOperatingNumberPlugin extends FilterConditionPluginImpl<
  GenericOperatingNumberValue,
  GenericPluginDto
> {
  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    private operatingNumberId: string | null = null,
    private comparison: ClinicOperatingNumberComparison = 'GTE',
    private value: number | null = null,
  ) {
    super(blueprint.id);
  }

  clone(): GenericOperatingNumberPlugin {
    return new GenericOperatingNumberPlugin(
      this.blueprint,
      this.operatingNumberId,
      this.comparison,
      this.value,
    );
  }

  /** New instance for the same blueprint with the given operating-number selection. */
  withValue(
    operatingNumberId: string | null,
    comparison: ClinicOperatingNumberComparison,
    value: number | null,
  ): GenericOperatingNumberPlugin {
    return new GenericOperatingNumberPlugin(
      this.blueprint,
      operatingNumberId,
      comparison,
      value,
    );
  }

  toCondition(): BdCondition | null {
    return new GenericOperatingNumberCondition(
      this.blueprint.id,
      this.operatingNumberId,
      this.comparison,
      this.value,
    );
  }

  toJSON(): GenericPluginDto | null {
    return {
      type: this.type,
      operatingNumberId: this.operatingNumberId,
      comparison: this.comparison,
      operatingNumberValue: this.value,
    };
  }

  toText(): string | null {
    return this._text();
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (!this.operatingNumberId) return {};
    return {
      [FILTER_RESOURCE_CLINIC_OPERATING_NUMBER]: [this.operatingNumberId],
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this._text(resolvedNames);
    if (!title) return null;
    return { title, items: [] };
  }

  /**
   * `Kennzahl: Apotheker ≥ 5` when the operating number's label is known (from
   * `resolvedNames`, i.e. the detailed-show resource resolver), otherwise
   * `Kennzahl ≥ 5`.
   *
   * The id is never shown: it is a technical key, and it cannot be turned into
   * a label either — the catalog's ids are English camelCase while its labels
   * are German (`beds` → "Klinik-Betten", `outpatientCases` → "Ambulante
   * Fälle"), so anything derived from one would read as English in a German UI.
   * Comparison plus value on their own already say more than the old text did.
   */
  private _text(resolvedNames?: Map<string, string>): string | null {
    if (!this.operatingNumberId || this.value == null) return null;
    const name = resolvedNames?.get(this.operatingNumberId);
    const comparison =
      CLINIC_OPERATING_NUMBER_COMPARISON_SYMBOLS[this.comparison];
    const label = blueprintDisplayName(this.blueprint);
    return name
      ? `${label}: ${name} ${comparison} ${this.value}`
      : `${label} ${comparison} ${this.value}`;
  }

  toValue(): GenericOperatingNumberValue {
    return {
      operatingNumberId: this.operatingNumberId,
      comparison: this.comparison,
      value: this.value,
    };
  }

  override valid(): boolean {
    return (
      !!this.operatingNumberId &&
      !!this.comparison &&
      this.value != null &&
      Number.isFinite(this.value)
    );
  }

  validationToText(): string {
    if (!this.operatingNumberId) return 'Kennzahl auswählen';
    if (this.value == null || !Number.isFinite(this.value)) {
      return 'Wert eingeben';
    }
    return '';
  }
}

/**
 * Wrapper (department / deqs) plugin — holds a nested condition tree evaluated
 * against ONE department / DeQS Leistungsbereich. `innerGrain` is the grain the
 * inner conditions must belong to (used by the UI to scope the offered list).
 */
export class GenericWrapperPlugin extends FilterConditionPluginImpl<
  FilterConditionExpression,
  GenericPluginDto
> {
  public readonly displayName: string;

  constructor(
    private readonly blueprint: ClinicConditionBlueprint,
    public readonly innerGrain: ClinicConditionTarget,
    private inner: FilterConditionExpression = FilterConditionUtil.EMPTY(),
  ) {
    super(blueprint.id);
    this.displayName = blueprintDisplayName(blueprint);
  }

  clone(): GenericWrapperPlugin {
    return new GenericWrapperPlugin(
      this.blueprint,
      this.innerGrain,
      this.inner.clone(),
    );
  }

  /** New instance for the same wrapper with a replaced inner tree. */
  withInner(inner: FilterConditionExpression): GenericWrapperPlugin {
    return new GenericWrapperPlugin(this.blueprint, this.innerGrain, inner.clone());
  }

  toCondition(): BdCondition | null {
    return new GenericWrapperCondition(this.blueprint.id, this.inner.toCondition());
  }

  toJSON(): GenericPluginDto | null {
    return { type: this.type, condition: this.inner.toJSON() };
  }

  toText(): string | null {
    return this.inner.valid() ? blueprintDisplayName(this.blueprint) : null;
  }

  /**
   * Forward the nested tree's resources so leaves inside the wrapper (e.g. a
   * Kennzahl on department grain) get their ids resolved to names as well.
   */
  override getExtraChildResources(): Record<string, string[]> {
    return this.inner.getExtraChildResources();
  }

  override getChildResources(): { [resourceName in ShareableType]?: string[] } {
    return this.inner.getChildResources();
  }

  toValue(): FilterConditionExpression {
    return this.inner;
  }

  override valid(): boolean {
    return this.inner.valid();
  }

  validationToText(): string {
    return this.valid() ? '' : 'Mindestens ein Kriterium hinzufügen';
  }
}

/**
 * Build a synthetic plugin specification for a blueprint, or `null` if this
 * blueprint is not handled by the generic branch. `operatingNumber` (OTHER with
 * `operatingNumberReference`) and the `department` / `deqs` wrappers (OTHER) are
 * handled; OPERATOR is a tree node.
 */
export function createGenericSpecification(
  blueprint: ClinicConditionBlueprint,
  sortingIndex = 0,
): GenericSpecification | null {
  const base = {
    type: blueprint.id,
    category: 'clinic' as const,
    displayName: blueprint.displayName,
    sortingIndex,
    occurrence: 'single' as const,
  };

  switch (blueprint.type) {
    case 'LIST':
      return {
        ...base,
        fromObject: (obj) =>
          new GenericListPlugin(blueprint, obj.includes ?? [], obj.excludes ?? []),
        fromCondition: (inner) =>
          new GenericListPlugin(
            blueprint,
            inner.includes ?? [],
            inner.excludes ?? [],
          ),
        newInstance: () => new GenericListPlugin(blueprint),
      };
    case 'RANGE':
      return {
        ...base,
        fromObject: (obj) =>
          new GenericRangePlugin(blueprint, obj.from ?? null, obj.to ?? null),
        fromCondition: (inner) =>
          new GenericRangePlugin(blueprint, inner.from ?? null, inner.to ?? null),
        newInstance: () => new GenericRangePlugin(blueprint),
      };
    case 'FLAG':
      return {
        ...base,
        fromObject: (obj) => new GenericFlagPlugin(blueprint, obj.flag ?? true),
        fromCondition: (inner) =>
          new GenericFlagPlugin(blueprint, inner.flag ?? true),
        newInstance: () => new GenericFlagPlugin(blueprint),
      };
    case 'SUBSTRING':
      return {
        ...base,
        fromObject: (obj) => new GenericSubstringPlugin(blueprint, obj.value ?? ''),
        fromCondition: (inner) =>
          new GenericSubstringPlugin(blueprint, String(inner.value ?? '')),
        newInstance: () => new GenericSubstringPlugin(blueprint),
      };
    case 'OTHER': {
      if (findFragment(blueprint, 'operatingNumberReference')) {
        return {
          ...base,
          category: 'case' as const,
          fromObject: (obj) =>
            new GenericOperatingNumberPlugin(
              blueprint,
              obj.operatingNumberId ?? null,
              obj.comparison ?? 'GTE',
              obj.operatingNumberValue ?? null,
            ),
          fromCondition: (inner) =>
            new GenericOperatingNumberPlugin(
              blueprint,
              inner.operatingNumberId ?? null,
              inner.comparison ?? 'GTE',
              inner.value == null ? null : Number(inner.value),
            ),
          newInstance: () => new GenericOperatingNumberPlugin(blueprint),
        };
      }
      const innerGrain = WRAPPER_INNER_GRAINS[blueprint.id];
      if (innerGrain) {
        return {
          ...base,
          // The in-memory (UI) build path and the internal object form start
          // with an empty inner tree — only the server condition JSON carries a
          // nested tree, rehydrated by `fromConditionAsync` below. `fromObject`
          // is never reached for a wrapper (the generic wrapper spec is only
          // registered for the clinics filter, whose load path is condition
          // JSON via `AysClinicsFilter.fromObj` → `fromConditionJson`).
          fromObject: () => new GenericWrapperPlugin(blueprint, innerGrain),
          fromCondition: () => new GenericWrapperPlugin(blueprint, innerGrain),
          fromConditionAsync: (inner, registeredFilter) => {
            const innerJson = inner?.condition;
            if (!innerJson) {
              return Promise.resolve(
                new GenericWrapperPlugin(blueprint, innerGrain),
              );
            }
            return FilterConditionUtil.fromConditionJson(
              innerJson,
              registeredFilter,
            )
              .then(
                (innerExpression) =>
                  new GenericWrapperPlugin(blueprint, innerGrain, innerExpression),
              )
              .catch(() =>
                // Degrade to an empty wrapper if an inner condition can't be
                // resolved (e.g. a removed/renamed condition id) rather than
                // failing the whole saved-filter load. Mirrors the FE hardening
                // for the clinics-filters-api id skew (PR #1738).
                new GenericWrapperPlugin(blueprint, innerGrain),
              );
          },
          newInstance: () => new GenericWrapperPlugin(blueprint, innerGrain),
        };
      }
      return null;
    }
    default:
      return null;
  }
}
